'use client'

import { IconChevronDown } from '@macarongfactory/web-icons'
import { BottomSheet, RightSelectRow, Text } from '@macarongfactory/web-ui'
import { useEffect, useMemo, useRef, useState } from 'react'

import type { TFuelType } from '../_features/modoocar'

/**
 * 유종 필터 — 차종 허브(CarBasicInfo.tsx)의 `FuelTypeChip` UX를 그대로 복제.
 *
 * 차종 허브는 export되지 않은 내부 컴포넌트이고 차종허브 라우팅/트래킹에 묶여 있어
 * 메이커스에서 import할 수 없으므로 동일 마크업/동작으로 복제한다.
 * 모두의 차고는 단일 페이지(목업)라 선택 시 라우트 이동 대신 onChange로 로컬 상태만 갱신한다.
 */

const FUEL_DIALOG_TITLE = '유종 선택'
const FUEL_DIALOG_CONFIRM = '확인'
const PC_MEDIA_QUERY = '(min-width: 1024px)'

/** 트리거 chevron 아이콘 크기(px) */
const CHEVRON_ICON_SIZE_PX = 14

/** 칩 트리거 공통 스타일 (높이/정렬/보더/배경) — 패딩만 chevron 유무로 분기 */
const TRIGGER_BASE_CLASS =
  'flex h-dimension-x8 items-center gap-dimension-x1 rounded-full border border-stroke-neutral-strong bg-background-surface-default disabled:cursor-default'

interface IFuelTypeFilterProps {
  value: TFuelType
  options: TFuelType[]
  onChange: (fuel: TFuelType) => void
  /** 필터칩(트리거) 클릭으로 시트/팝오버가 열릴 때 호출 (클릭 트래킹용) */
  onOpen?: () => void
}

/** 유종 옵션 리스트 (BottomSheet 본문) */
const FuelOptionList = ({
  options,
  selectedFuel,
  onSelect,
}: {
  options: TFuelType[]
  selectedFuel: TFuelType
  onSelect: (fuel: TFuelType) => void
}) => {
  return (
    <div className='flex flex-col'>
      {options.map(option => {
        const isSelected = option === selectedFuel
        return (
          <RightSelectRow
            key={option}
            verticalPadding='dimension-x3'
            title={
              // 선택 여부와 무관하게 동일 타이포/색 — 선택 표시는 체크마크만 (Figma 7561:5972)
              <RightSelectRow.Main.Title.Text
                text={option}
                typography='title-1-medium'
                color='text-foreground-neutral-normal'
              />
            }
            right={<RightSelectRow.Right.Checkmark />}
            selected={isSelected}
            clickable={() => onSelect(option)}
          />
        )
      })}
    </div>
  )
}

const FuelTypeFilter = ({
  value,
  options,
  onChange,
  onOpen,
}: IFuelTypeFilterProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [tempFuel, setTempFuel] = useState<TFuelType>(value)
  const containerRef = useRef<HTMLDivElement | null>(null)

  const displayOptions = useMemo(
    () => (options.length === 0 ? [value] : options),
    [options, value]
  )
  const canSwitchFuel = displayOptions.length > 1

  const handleOpen = () => {
    if (!canSwitchFuel) return
    onOpen?.()
    setTempFuel(value)
    setIsOpen(true)
  }
  const handleClose = () => setIsOpen(false)
  // 확인 시 유종이 변경된 경우에만 적용(URL 리다이렉션) — 동일 유종이면 닫기만 (Figma 7561:5972)
  const handleMobileConfirm = () => {
    setIsOpen(false)
    if (tempFuel !== value) onChange(tempFuel)
  }
  const handlePcSelect = (fuel: TFuelType) => {
    setIsOpen(false)
    if (fuel !== value) onChange(fuel)
  }

  // PC 팝오버: 바깥 클릭 시 닫기 (모바일 BottomSheet는 자체 backdrop으로 닫힘)
  useEffect(
    function closePcPopoverOnOutsideClick() {
      if (!isOpen) return
      if (typeof window === 'undefined') return
      if (!window.matchMedia(PC_MEDIA_QUERY).matches) return

      const handlePointerDown = (event: PointerEvent) => {
        const target = event.target as Node | null
        if (!target) return
        if (containerRef.current?.contains(target)) return
        setIsOpen(false)
      }
      window.addEventListener('pointerdown', handlePointerDown)
      return () => window.removeEventListener('pointerdown', handlePointerDown)
    },
    [isOpen]
  )

  return (
    <div className='shrink-0'>
      <div ref={containerRef} className='relative inline-block'>
        <button
          type='button'
          aria-haspopup='listbox'
          aria-expanded={isOpen}
          onClick={handleOpen}
          disabled={!canSwitchFuel}
          className={`${TRIGGER_BASE_CLASS} ${
            canSwitchFuel
              ? 'pl-dimension-x3 pr-dimension-x2'
              : 'px-dimension-x3'
          }`}
        >
          <Text
            variant='body-2-semibold'
            color='text-foreground-neutral-normal'
          >
            {value}
          </Text>
          {canSwitchFuel ? (
            <IconChevronDown
              width={CHEVRON_ICON_SIZE_PX}
              height={CHEVRON_ICON_SIZE_PX}
              className='text-foreground-neutral-normal'
            />
          ) : null}
        </button>

        {isOpen ? (
          <ul
            aria-label={FUEL_DIALOG_TITLE}
            className='shadow-weak absolute left-0 top-[calc(100%+8px)] z-10 hidden min-w-[120px] flex-col rounded-dimension-x3 bg-background-surface-default py-dimension-x2 lg:flex'
          >
            {displayOptions.map(option => {
              const isSelected = option === value
              return (
                <li key={option}>
                  <button
                    type='button'
                    aria-current={isSelected ? 'true' : undefined}
                    onClick={() => handlePcSelect(option)}
                    className='flex w-full items-center px-dimension-x4 py-dimension-x3 text-left hover:bg-background-neutral-weak'
                  >
                    <Text
                      variant={
                        isSelected ? 'title-2-semibold' : 'title-2-medium'
                      }
                      color={
                        isSelected
                          ? 'text-foreground-brand-strong'
                          : 'text-foreground-neutral-normal'
                      }
                    >
                      {option}
                    </Text>
                  </button>
                </li>
              )
            })}
          </ul>
        ) : null}
      </div>

      <div className='lg:hidden'>
        <BottomSheet
          open={isOpen}
          onClose={handleClose}
          className='left-0 right-0 mx-auto max-w-[480px]'
          top={
            <BottomSheet.Top.LeftAlignedHeader
              title={FUEL_DIALOG_TITLE}
              titleTypography='heading-3-bold'
            />
          }
          content={
            <BottomSheet.Content>
              <FuelOptionList
                options={displayOptions}
                selectedFuel={tempFuel}
                onSelect={setTempFuel}
              />
            </BottomSheet.Content>
          }
          bottom={
            // 디자인(7561:5972)엔 상단 그라데이션(transparent 페이드)이 없음.
            // 버튼 하단 여백은 시트 컨테이너 pb가 담당(DS 26.6.0-5부터 CTA pb 0이 기본)
            <BottomSheet.Bottom.BottomCTA.Vertical
              gradation={false}
              verticalPadding='pt-dimension-x5'
              mainButton={
                <BottomSheet.Bottom.MainButton.SolidButton
                  text={FUEL_DIALOG_CONFIRM}
                  onClick={handleMobileConfirm}
                />
              }
            />
          }
        />
      </div>
    </div>
  )
}

export default FuelTypeFilter
